import { useEffect, useRef, useState } from "react";
import { auth, ApiError } from "./api";
import { CSS, ThemeToggle } from "./AssetInventory";

/* ==========================================================================
   Sign-in page.

   Renders whichever methods the server currently offers, from
   GET /api/auth/options — it does not decide policy, it reflects it. The
   password form disappears when SSO is enabled because the server stops
   accepting passwords, not because this file hides a button.
   ========================================================================== */

function MsLogo() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
      <rect x="0" y="0" width="7" height="7" fill="#F25022" />
      <rect x="9" y="0" width="7" height="7" fill="#7FBA00" />
      <rect x="0" y="9" width="7" height="7" fill="#00A4EF" />
      <rect x="9" y="9" width="7" height="7" fill="#FFB900" />
    </svg>
  );
}

// Reads ?error= once, then strips it from the URL so a refresh does not
// resurrect a stale message. The SSO callback redirects here on every failure.
function useQueryError() {
  const [error, setError] = useState(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const value = params.get("error");
    if (!value) return;
    setError(value);
    params.delete("error");
    const query = params.toString();
    window.history.replaceState(
      {},
      "",
      window.location.pathname + (query ? `?${query}` : ""),
    );
  }, []);
  return [error, setError];
}

export default function Login({ onSignedIn }) {
  const [options, setOptions] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [error, setError] = useQueryError();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const emailRef = useRef(null);

  useEffect(() => {
    let live = true;
    auth
      .options()
      .then((o) => live && setOptions(o))
      .catch((err) => live && setLoadError(err.message));
    return () => {
      live = false;
    };
  }, []);

  // Focus the first field once we know there is one to focus.
  useEffect(() => {
    if (options?.password) emailRef.current?.focus();
  }, [options?.password]);

  async function submitPassword(e) {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const user = await auth.login(email.trim(), password);
      onSignedIn?.(user);
    } catch (err) {
      setError(err.message);
      // Keep the email, clear the password — the usual reason to be here is a
      // mistyped password, and retyping the address as well is just friction.
      setPassword("");
      if (err instanceof ApiError && err.status === 401) emailRef.current?.focus();
    } finally {
      setSubmitting(false);
    }
  }

  function startSso() {
    setRedirecting(true);
    // A full navigation, not a fetch: the response is a 302 to
    // login.microsoftonline.com and fetch cannot follow it usefully.
    window.location.href = auth.ssoStartUrl();
  }

  const ssoOn = !!options?.sso;
  const passwordOn = !!options?.password;

  return (
    <div className="ai-root">
      <style>{CSS}</style>
      <style>{LOGIN_CSS}</style>

      <div className="ai-gate">
        <div className="ai-login-theme">
          <ThemeToggle />
        </div>
        <div className="ai-gate-card ai-gate-card-solo">
          <div className="ai-login-body">
            <div className="ai-brand" style={{ marginBottom: 18 }}>
              <span className="ai-brand-mark">◆</span>
              <span className="ai-brand-name">Asset Inventory</span>
              <span className="ai-brand-org">Solutions Exchange Inc</span>
            </div>

            <h1 className="ai-gate-title">Sign in</h1>
            <p className="ai-gate-sub">
              Scan a property tag to see who holds the device, whether they are
              still with the company, and how long they have had it.
            </p>

            {error && (
              <p className="ai-login-alert ai-login-alert-bad" role="alert">
                {error}
              </p>
            )}

            {loadError && (
              <p className="ai-login-alert ai-login-alert-bad" role="alert">
                {loadError}
              </p>
            )}

            {!options && !loadError && (
              <p className="ai-login-muted">Checking available sign-in methods…</p>
            )}

            {ssoOn && (
              <button
                className="ai-btn ai-btn-ms"
                onClick={startSso}
                disabled={redirecting}
              >
                <MsLogo />
                {redirecting ? "Redirecting to Microsoft…" : "Sign in with Microsoft"}
              </button>
            )}

            {ssoOn && passwordOn && (
              <div className="ai-login-or">
                <span />
                or
                <span />
              </div>
            )}

            {passwordOn && (
              <form onSubmit={submitPassword} noValidate>
                <label className="ai-field-label" htmlFor="login-email">
                  Email
                </label>
                <input
                  id="login-email"
                  ref={emailRef}
                  className="ai-input"
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@assets.local"
                />

                <label className="ai-field-label" htmlFor="login-password">
                  Password
                </label>
                <input
                  id="login-password"
                  className="ai-input"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                />

                <button
                  className="ai-btn ai-btn-primary ai-full"
                  type="submit"
                  disabled={submitting || !email.trim() || !password}
                >
                  {submitting ? "Signing in…" : "Sign in"}
                </button>
              </form>
            )}

            {options?.forced && (
              <p className="ai-login-alert ai-login-alert-warn">
                <strong>Break-glass mode.</strong> The password login is only
                available because <code>AUTH_FORCE_PASSWORD_LOGIN</code> is set in
                the server environment. Unset it and restart once Microsoft
                sign-in is working.
              </p>
            )}

            {options && !ssoOn && !passwordOn && (
              <p className="ai-login-alert ai-login-alert-warn">
                No sign-in method is enabled. Set{" "}
                <code>AUTH_FORCE_PASSWORD_LOGIN=true</code> in{" "}
                <code>server/.env</code> and restart to restore the password
                login.
              </p>
            )}

            {options && ssoOn && !passwordOn && (
              <p className="ai-note">
                Use your Solutions Exchange Microsoft 365 account. If it is not
                recognised, ask ITSC to add you — accounts are not created
                automatically.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* Login-only styles. Everything else comes from the shared CSS in
   AssetInventory.jsx, so the tokens are defined in exactly one place. */
const LOGIN_CSS = `
/* Parcel's HTML entry ships no CSS reset, and there is no index.css, so the
   UA's default body margin would put an 8px gutter around a 100vh layout and
   produce a scrollbar on a page that fits. */
body { margin: 0; }

/* The two-column .ai-gate-card expects a printed property tag on the left. The
   sign-in page has no asset to show, so it runs as a single column. */
.ai-gate { position: relative; }
.ai-login-theme { position: absolute; top: 18px; right: 18px; }

.ai-gate-card-solo { grid-template-columns: 1fr; max-width: 420px; }
.ai-login-body { padding: 36px; }

.ai-login-muted { color: var(--zinc); font-size: 13px; margin: 0; }

.ai-login-or {
  display: flex; align-items: center; gap: 12px;
  margin: 18px 0; font-size: 11px; color: var(--zinc);
  font-family: var(--mono); letter-spacing: 0.1em; text-transform: uppercase;
}
.ai-login-or > span { flex: 1; height: 1px; background: var(--rule); }

.ai-login-alert {
  font-size: 13px; line-height: 1.45; margin: 0 0 16px;
  padding: 10px 12px; border-radius: 3px; border: 1px solid;
}
.ai-login-alert code {
  font-family: var(--mono); font-size: 11px;
  background: rgba(0, 0, 0, 0.05); padding: 1px 4px; border-radius: 2px;
}
.ai-login-alert-bad  { background: var(--red-bg);   border-color: #E8C4BE; color: var(--red); }
.ai-login-alert-warn { background: var(--amber-bg); border-color: #E6D2AF; color: var(--amber); }

/* The break-glass and no-method notices sit below the form, not above it. */
form + .ai-login-alert, .ai-btn-ms + .ai-login-alert { margin: 18px 0 0; }

@media (max-width: 480px) {
  .ai-login-body { padding: 28px 22px; }
}
`;
